import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { XMarkIcon, GiftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import Button from './ui/Button';
import Input from './ui/Input';

interface PromoPopupProps {
  promoCode: string;
  isOpen: boolean;
  onClose: () => void;
}

const PROMO_DISMISSED_KEY = 'medobill_promo_dismissed';

const PromoPopup: React.FC<PromoPopupProps> = ({ promoCode, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [promoInput, setPromoInput] = useState(promoCode);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setPromoInput(promoCode);
  }, [promoCode]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  const handleClose = () => {
    sessionStorage.setItem(PROMO_DISMISSED_KEY, 'true');
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const normalizedPromo = promoInput.trim().toUpperCase();
    if (!normalizedPromo) {
      setError('Please enter your promo code');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      sessionStorage.setItem('promoCode', normalizedPromo);
      sessionStorage.setItem('promoEmail', email.trim());
      sessionStorage.setItem(PROMO_DISMISSED_KEY, 'true');

      navigate(`/auth/login?promo=${encodeURIComponent(normalizedPromo)}&email=${encodeURIComponent(email.trim())}`);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/20 bg-theme-card shadow-[0_24px_60px_rgba(0,0,0,0.25)]">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(136,197,195,0.15) 0%, rgba(160,212,210,0.1) 50%, rgba(138,43,226,0.08) 100%)',
          }}
        />

        <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-gradient-to-br from-purple-400/30 to-cyan-400/20 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-32 h-32 rounded-full bg-gradient-to-tr from-teal-400/25 to-purple-400/15 blur-3xl" />

        <div className="relative z-10 p-6 sm:p-8">
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-full p-2 text-theme-text-muted transition hover:bg-black/5 hover:text-theme-text dark:hover:bg-white/10"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#88C5C3] to-[#A0D4D2] shadow-lg">
                <GiftIcon className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-theme-accent shadow-md">
                <SparklesIcon className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-theme-text mb-2">
              You&apos;ve been invited!
            </h2>
            <p className="text-theme-text-muted text-sm sm:text-base">
              Try{' '}
              <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#88C5C3] to-[#A0D4D2]">
                MedoBill
              </span>{' '}
              free for{' '}
              <span className="font-bold text-theme-accent">30 days</span>
            </p>
            <p className="text-theme-text-muted text-xs mt-2">
              Includes 500 bonus credits to start calling right away
            </p>
          </div>

          <div className="mb-5 rounded-xl border border-[#88C5C3]/30 bg-gradient-to-r from-[#88C5C3]/10 to-[#A0D4D2]/10 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-[#88C5C3]">
                Promo Code
              </span>
            </div>
            <Input
              type="text"
              placeholder="Enter promo code"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              className="mt-2"
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder="Enter your work email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              {...(error ? { error } : {})}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isSubmitting}
              className="w-full"
            >
              Continue to Login
            </Button>
          </form>

          <p className="text-center text-xs text-theme-text-muted mt-4">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-[#88C5C3] hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-[#88C5C3] hover:underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PromoPopup;
export { PROMO_DISMISSED_KEY };
